import z from 'zod';

import { ItemStatusEnum, PrinterTypeEnum } from '@/modules/common';
import { FileObject, OptionType } from '@/modules/common/types';
import { Mask } from '@/modules/masks/types';
import { Model } from '@/modules/models/types';
import { OverlayItem } from '@/modules/overlays/types';

import {
  EventDetailsTab,
  EventModelPosition,
  EventParticipantStatus,
  EventPrinterStatus,
} from '../constants';
import { stationsSchema } from '../validations';

export enum EventQueryKeys {
  Stations = 'eventStations',
  ProductSets = 'eventProductSets',
}

export interface EventSystemPromptResponse {
  id: string;
  eventId: string;
  systemPromptId: string;
  content: string;
  notes: string;
  systemPromptCategory: string | null;
  systemPromptVersion: number;
  systemPromptContent: string;
  surpriseMeContent: string;
}

export interface Event {
  id: string;
  name: string;
  startDate: string;
  aiModel: string;
  status: ItemStatusEnum;
}

export interface EventType {
  id: string;
  name: string;
  tabs: EventDetailsTab[];
  requiredFields: {
    [key: string]: string[];
  };
}
export interface EventsResponse {
  items: Event[];
}

export interface EventUploadUrlResponse {
  url: string;
  key: string;
}

export interface UpdateEventMediaRequest {
  videoKey?: string | null;
  idleStateVideoKey?: string | null;
  backgroundImageKey?: string | null;
}

export interface MediaField {
  value: File | string;
  isDirty: boolean;
}

export interface SaveMediaInput {
  video: MediaField;
  idleStateVideo: MediaField;
  backgroundImage: MediaField;
}

export interface EventPrinter {
  name: string;
  type: PrinterTypeEnum;
  printerId: string;
  status: EventPrinterStatus;
}

export interface LacesColor {
  id?: string;
  name: string;
  hex: string;
  quantity: number;
}

export interface CollarColor {
  id?: string;
  name: string;
  hex: string;
}

export interface EventModelSize {
  quantity: string;
  sizeId: string;
}

export interface EventStyle {
  id: string;
  name: string;
  imagePublicUrl: string;
  fileObject?: { readUrl: string };
}

export interface EventTextStyle {
  id: string;
  name: string;
  status: string;
  content: string;
}

export interface EventModelDetails {
  model: Model;
  sizes: {
    quantity: number;
    reservedQuantity: number;
    size: OptionType;
  }[];
  sides: EventModelPosition[];
}

export interface EventMask extends Mask {
  overlays: OverlayItem;
}

export interface EventFeatureFlagsResponse {
  isGeneratedImageCodeEnabled: boolean;
  isPromoSmsEnabled: boolean;
  signatureEnabled: boolean;
  remixingEnabled: boolean;
  groupOrderingEnabled: boolean;
  gptEnabled: boolean;
  surpriseMeEnabled: boolean;
  rectangleImageGenerationEnabled: boolean;
  isFadedHeelEnabled: boolean;
  isShoeSizeHintEnabled: boolean;
}

export interface EventErrorMessages {
  shoeSizesHintMessage: string | null;
}

export interface EventDetails extends Event {
  publicUrl: string;
  idleStateVideoPublicUrl: string;
  printers?: EventPrinter[];
  lacesColors?: LacesColor[];
  collarColors?: CollarColor[];
  desktopSecretKey: string;
  iosSecretKey: string;
  models: EventModelDetails[];
  masks: EventMask[];
  styles: EventStyle[];
  textStyles: EventTextStyle[];
  videoFileObject: FileObject | null;
  idleStateVideoFileObject: FileObject | null;
  backgroundImageFileObject: FileObject | null;
  types: EventType[];
  validationErrors: Record<string, string[]>;
  errorMessages: EventErrorMessages;
  featureFlags: EventFeatureFlagsResponse;
  eventSystemPrompt: EventSystemPromptResponse | null;
  securityPrompt?: string;
}

export interface CreateEventPayload {
  name: string;
  startDate: string;
  types: { typeId: string }[];
}

export interface UpdateEventPayload {
  id: string;
  status: ItemStatusEnum;
  name: string;
  aiModel: string;
  startDate: string;
  gptEnabled: boolean;
  idleStateVideoPublicUrl: string;
  remixingEnabled: boolean;
  groupOrderingEnabled: boolean;
  isGeneratedImageCodeEnabled: boolean;
  isPromoSmsEnabled: boolean;
  surpriseMeEnabled?: boolean;
  rectangleImageGenerationEnabled?: boolean;
  isFadedHeelEnabled?: boolean;
  isShoeSizeHintEnabled?: boolean;
  printers?: EventPrinter[];
  lacesColors?: LacesColor[];
  collarColors?: CollarColor[];
  signatureEnabled?: boolean;
  models: {
    modelId: string;
    sizes: EventModelSize[];
    sides: EventModelPosition[];
  }[];
  styles: {
    styleId: string;
  }[];
  textStyles?: {
    textStyleId: string;
  }[];
  masks: {
    maskId: string;
    overlays: { overlayId: string }[];
  }[];
  types: {
    typeId: string;
  }[];
  systemPromptId?: string;
  systemPromptContent?: string;
  systemPromptNotes?: string;
  surpriseMeContent?: string;
  securityPrompt?: string;
}

export interface EventParticipant {
  id: string;
  firstName: string;
  lastName: string;
  usagesLeft: number;
  email: string;
  company: string;
  jobTitle: string;
  phoneNumber: string;
  comment: string;
  type: string;
  externalId?: string;
  status: EventParticipantStatus;
  notificationNumbers: string[];
}

export interface EventParticipantsResponse {
  items: EventParticipant[];
}

export type Size = {
  id: string;
  size: string;
  amount: number;
};

export interface EventProductResponseItem {
  id: string;
  name: string;
  category: string;
  nickname: string;
  styleNumber: string;
  totalAmount: number;
  sizes: Size[];
  createdAt: string;
}

export interface EventProduct extends EventProductResponseItem {
  size: Size;
}

export interface EventProductsResponse {
  items: EventProductResponseItem[];
}

export interface ProductSetProduct {
  id: string;
  productId: string;
  nickname: string;
  styleNumber: string;
  category: string;
  imageUrl: string | null;
  size: string;
  amount: number;
}

export interface ProductSet {
  id: string;
  name: string;
  products: ProductSetProduct[];
  createdAt: string;
}

export interface CreateProductSetPayload {
  name: string;
  products: {
    productId: string;
    size: string;
    amount: number;
  }[];
}

export interface UpdateProductSetPayload {
  name: string;
  // Full desired contents of the set: items with an existing id are updated,
  // items without one are added, and existing items left out are removed.
  products: {
    id?: string;
    productId: string;
    size: string;
    amount: number;
  }[];
}

export interface ProductSetConflictsPayload {
  // The set being edited, so its own items are not reported against itself.
  excludeProductSetId?: string;
  items: {
    productId: string;
    size: string;
  }[];
}

// Only the product/size pairs that actually clash come back.
export interface ProductSetConflict {
  productId: string;
  size: string;
  sets: {
    id: string;
    name: string;
  }[];
}

export interface EventParticipantType {
  eventId: string;
  id: string;
  value: string;
  notificationNumbers: {
    id: string;
    phoneNumber: string;
    typeId: string;
  }[];
}

export interface CreateEventParticipantTypePayload {
  types: {
    value: string;
    phoneNumbers: string[];
  }[];
}

export interface EventStationResponseItem {
  id: string;
  name: string;
  accessTypes: {
    id: string;
    name: string;
  }[];
}

export interface MutateStationPayload {
  id: string;
  name: string;
  accessTypeIds: string[];
}

export type StationsFormValues = z.infer<typeof stationsSchema>;

export interface SendSmsPayload {
  message: string;
  participantIds: string[];
}

export interface SendOrderSmsPayload {
  message: string;
  orderIds: string[];
}

export enum EventOrderStatus {
  New = 'NEW',
  Pending = 'PENDING',
  Printing = 'PRINTING',
  Completed = 'COMPLETED',
  Canceled = 'CANCELED',
  Failed = 'FAILED',
}

export enum EventSmsStatus {
  Sent = 'sent',
  Failed = 'failed',
}

export interface EventOrder {
  id: string;
  name: string;
  color: string;
  createdAt: Date;
  firstName: string;
  gender: string;
  imageUrl: string;
  lastName: string;
  number: number;
  phoneNumber: string;
  size: string;
  status: EventOrderStatus;
  type: string;
  updatedAt: Date;
  groupId: string;
}

export interface EventSms {
  message: string;
  status: EventSmsStatus;
  phoneNumber: 'string';
  createdAt: Date;
}
